import { BACKEND_URL } from "../../lib/backend";
/**
 * ReportSourcesPanel — manage the curated "Reports & Research" source
 * organisations (collection `report_sources`). Self-contained: loads its own
 * data, supports Add / Edit / Delete, search and priority filtering.
 * The global axios interceptor (AuthContext) attaches the CSRF header.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { ReportPipelineCard } from "./ReportPipelineCard";
import {
  RefreshCw, Check, Plus, X, Trash2, Pencil, ExternalLink, Search, BookOpen, Radar, Link2, ListPlus,
} from "lucide-react";

const API = `${BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  Alta:  { bgL: "#fee2e2", bgD: "#3f1414", fgL: "#991b1b", fgD: "#fca5a5" },
  Media: { bgL: "#fef3c7", bgD: "#3f2e0e", fgL: "#92400e", fgD: "#fcd34d" },
  Bassa: { bgL: "#e5e7eb", bgD: "#2a3132", fgL: "#374151", fgD: "#cbd5e1" },
};

const EMPTY_FORM = {
  organisation: "", category_macro: "", homepage_url: "", report_hub_url: "",
  rss_url: "", area: "", focus: "", priority: "Media", notes: "",
};

function SourceForm({ dm, form, setForm, onSave, onCancel, saving, idPrefix }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = `text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-white placeholder:text-[#bac9cc]"}`;
  return (
    <div className="space-y-2" data-testid={`${idPrefix}-form`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input value={form.organisation} onChange={set("organisation")} placeholder="Organisation name *" data-testid={`${idPrefix}-organisation`} className={inputCls} />
        <Input value={form.category_macro} onChange={set("category_macro")} placeholder="Category (e.g. Travel tech, Istituzioni globali)" data-testid={`${idPrefix}-category`} className={inputCls} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input value={form.homepage_url} onChange={set("homepage_url")} placeholder="Homepage URL" data-testid={`${idPrefix}-homepage-url`} className={inputCls} />
        <Input value={form.report_hub_url} onChange={set("report_hub_url")} placeholder="Report hub URL (page listing reports — used by discovery)" data-testid={`${idPrefix}-hub-url`} className={inputCls} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input value={form.rss_url} onChange={set("rss_url")} placeholder="RSS URL (optional)" data-testid={`${idPrefix}-rss-url`} className={inputCls} />
        <Input value={form.area} onChange={set("area")} placeholder="Area (e.g. Global, Europe, Italia)" data-testid={`${idPrefix}-area`} className={inputCls} />
      </div>
      <Input value={form.focus} onChange={set("focus")} placeholder="Focus (e.g. Hospitality, AI, travel planning)" data-testid={`${idPrefix}-focus`} className={inputCls} />
      <Input value={form.notes} onChange={set("notes")} placeholder="Notes (optional)" data-testid={`${idPrefix}-notes`} className={inputCls} />
      <div className="flex items-center gap-2">
        <select
          value={form.priority}
          onChange={set("priority")}
          data-testid={`${idPrefix}-priority`}
          className={`flex-1 text-sm rounded-md px-3 py-2 border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-white text-[#191c1d]"}`}
        >
          <option value="Alta">Alta priority</option>
          <option value="Media">Media priority</option>
          <option value="Bassa">Bassa priority</option>
        </select>
        <button
          onClick={onSave}
          disabled={!form.organisation.trim() || saving}
          data-testid={`${idPrefix}-save-btn`}
          className={`flex items-center gap-1 px-4 py-2 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
        >
          {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Save
        </button>
        <button
          onClick={onCancel}
          data-testid={`${idPrefix}-cancel-btn`}
          className={`px-3 py-2 rounded-md category-tag transition-colors ${dm ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]"}`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function ReportSourcesPanel({ dm }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [scanningId, setScanningId] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  // Bulk URL import (one URL per line)
  const [bulkUrls, setBulkUrls] = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reports/sources?limit=1000`);
      setSources(r.data?.sources || []);
    } catch (e) {
      toast.error("Failed to load report sources");
    } finally {
      setLoading(false);
    }
  }, []);

   
  useEffect(() => { load(); }, [load]);

  const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

  const createSource = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/reports/sources`, {
        ...addForm,
        organisation: addForm.organisation.trim(),
        category_macro: addForm.category_macro.trim() || "Altro",
      });
      toast.success(`Source "${addForm.organisation.trim()}" added`);
      setAddForm(EMPTY_FORM);
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to add source"));
    } finally {
      setSaving(false);
    }
  };

  const updateSource = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/admin/reports/sources/${id}`, patch);
      setSources((prev) => prev.map((s) => (s.id === id ? r.data : s)));
      return true;
    } catch (e) {
      toast.error(errMsg(e, "Failed to update source"));
      return false;
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const ok = await updateSource(editingId, {
        organisation: editForm.organisation.trim(),
        category_macro: editForm.category_macro.trim() || "Altro",
        homepage_url: editForm.homepage_url,
        report_hub_url: editForm.report_hub_url,
        rss_url: editForm.rss_url,
        area: editForm.area,
        focus: editForm.focus,
        priority: editForm.priority,
        notes: editForm.notes,
      });
      if (ok) {
        toast.success("Source updated");
        setEditingId(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (src) => {
    if (!window.confirm(`Delete source "${src.organisation}"? Already-discovered reports are kept.`)) return;
    try {
      await axios.delete(`${API}/admin/reports/sources/${src.id}`);
      setSources((prev) => prev.filter((s) => s.id !== src.id));
      toast.success(`Source "${src.organisation}" deleted`);
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete source"));
    }
  };

  const scanSource = async (src) => {
    setScanningId(src.id);
    toast.info(`Scanning "${src.organisation}"… (Firecrawl, up to ~60s)`);
    try {
      const r = await axios.post(`${API}/admin/reports/sources/${src.id}/scan`, {}, { timeout: 120000 });
      const d = r.data || {};
      const n = d.reports_inserted || 0;
      if (n > 0) {
        toast.success(`"${src.organisation}": ${n} new report${n === 1 ? "" : "s"} added — AI abstract enrichment started in background`);
      } else {
        toast.success(`"${src.organisation}": no new reports found (${d.candidates_found || 0} candidates checked)`);
      }
      load();
    } catch (e) {
      toast.error(errMsg(e, "Scan failed"));
    } finally {
      setScanningId(null);
    }
  };

  const importByUrl = async () => {
    const u = importUrl.trim();
    if (!u) return;
    setImporting(true);
    toast.info("Importing report… (Firecrawl scrape, up to ~30s)");
    try {
      const r = await axios.post(`${API}/admin/reports/ingest-url`, { url: u }, { timeout: 120000 });
      const d = r.data || {};
      if (d.status === "duplicate") {
        toast.info(`Already in the library: "${(d.title || u).slice(0, 80)}"`);
      } else {
        toast.success(`Report imported: "${(d.title || u).slice(0, 80)}" (${d.organisation}) — AI abstract enrichment started`);
      }
      setImportUrl("");
    } catch (e) {
      toast.error(errMsg(e, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  // Bulk import: textarea with one URL per line. URLs are deduped server-side
  // and each one runs through the same `ingest-url` flow with concurrency=3 to
  // respect Firecrawl rate limits. Hard cap: 50 URLs per call.
  const bulkUrlsList = () => {
    return (bulkUrls || "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.startsWith("http://") || s.startsWith("https://"));
  };

  const bulkImport = async () => {
    const urls = bulkUrlsList();
    if (!urls.length) {
      toast.error("Paste at least one http(s):// URL — one per line");
      return;
    }
    if (urls.length > 50) {
      toast.info(`Importing first 50 URLs (cap per call); ${urls.length - 50} will be skipped`);
    }
    setBulkImporting(true);
    setBulkResult(null);
    toast.info(`Importing ${Math.min(urls.length, 50)} URL${urls.length === 1 ? "" : "s"}… (Firecrawl, may take a few minutes)`);
    try {
      const r = await axios.post(
        `${API}/admin/reports/ingest-urls-bulk`,
        { urls },
        { timeout: 600000 },  // 10 min — Firecrawl is slow on big batches
      );
      const d = r.data || {};
      setBulkResult(d);
      const parts = [];
      if (d.imported_count) parts.push(`${d.imported_count} imported`);
      if (d.duplicate_count) parts.push(`${d.duplicate_count} duplicate`);
      if (d.error_count) parts.push(`${d.error_count} error${d.error_count === 1 ? "" : "s"}`);
      if (d.skipped_overflow_count) parts.push(`${d.skipped_overflow_count} skipped (over 50-cap)`);
      toast.success(`Bulk done — ${parts.join(", ") || "no changes"} (AI enrichment started)`);
      if (d.imported_count) setBulkUrls("");  // clear input on success
    } catch (e) {
      toast.error(errMsg(e, "Bulk import failed"));
    } finally {
      setBulkImporting(false);
    }
  };

  const startEdit = (src) => {
    setEditingId(src.id);
    setEditForm({
      organisation: src.organisation || "",
      category_macro: src.category_macro || "",
      homepage_url: src.homepage_url || "",
      report_hub_url: src.report_hub_url || "",
      rss_url: src.rss_url || "",
      area: src.area || "",
      focus: src.focus || "",
      priority: src.priority || "Media",
      notes: src.notes || "",
    });
  };

  const counts = sources.reduce((acc, s) => {
    acc[s.priority] = (acc[s.priority] || 0) + 1;
    return acc;
  }, {});

  const q = search.trim().toLowerCase();
  const filtered = sources.filter((s) => {
    if (priorityFilter !== "all" && s.priority !== priorityFilter) return false;
    if (!q) return true;
    return [s.organisation, s.category_macro, s.focus, s.area]
      .some((f) => (f || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6" data-testid="report-sources-panel">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total", value: sources.length, fgL: "#006874", fgD: "#22d3ee" },
          { label: "Alta", value: counts.Alta || 0, fgL: PRIORITY_STYLES.Alta.fgL, fgD: PRIORITY_STYLES.Alta.fgD },
          { label: "Media", value: counts.Media || 0, fgL: PRIORITY_STYLES.Media.fgL, fgD: PRIORITY_STYLES.Media.fgD },
          { label: "Bassa", value: counts.Bassa || 0, fgL: PRIORITY_STYLES.Bassa.fgL, fgD: PRIORITY_STYLES.Bassa.fgD },
        ].map((c) => (
          <div key={c.label} className={`rounded-2xl p-4 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
            <p className={`category-tag mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{c.label}</p>
            <p className="text-3xl font-semibold" style={{ color: dm ? c.fgD : c.fgL, fontFamily: "'Reckless Neue Variable', serif" }} data-testid={`rs-count-${c.label.toLowerCase()}`}>
              {c.value}
            </p>
          </div>
        ))}
      </div>

      {/* Pipeline health + manual triggers (iter168) */}
      <ReportPipelineCard dm={dm} onChanged={load} />

      {/* Import report by URL */}
      <div className={`rounded-2xl p-5 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }} data-testid="rs-import-url-card">
        <div className="flex items-start gap-3 mb-3">
          <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
            <Link2 className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Import report by URL</p>
            <p className={`text-xs leading-relaxed mt-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              Know a specific report the scan missed? Paste its URL — it&apos;s scraped with Firecrawl, auto-linked to the matching source by domain, and the AI abstract is generated automatically.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && importUrl.trim() && !importing) importByUrl(); }}
            placeholder="https://news.example.com/2026-industry-report/"
            data-testid="rs-import-url-input"
            className={`text-sm border-0 ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`}
          />
          <button
            onClick={importByUrl}
            disabled={!importUrl.trim() || importing}
            data-testid="rs-import-url-btn"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md category-tag transition-colors disabled:opacity-50 flex-shrink-0 ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
          >
            {importing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Import
          </button>
        </div>
      </div>

      {/* Bulk import — one URL per line */}
      <div className={`rounded-2xl p-5 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }} data-testid="rs-bulk-import-card">
        <div className="flex items-start gap-3 mb-3">
          <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
            <ListPlus className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Bulk import — one URL per line</p>
            <p className={`text-xs leading-relaxed mt-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              Paste up to 50 report URLs (one per line). Each is scraped, auto-linked to a source by domain, and AI-summarised in the background. Concurrency is capped at 3 to respect Firecrawl limits.
            </p>
          </div>
        </div>
        <textarea
          value={bulkUrls}
          onChange={(e) => setBulkUrls(e.target.value)}
          placeholder={"https://news.example.com/report-1/\nhttps://research.example.org/2026-outlook/\nhttps://industry.example.com/whitepaper.pdf"}
          rows={5}
          data-testid="rs-bulk-import-textarea"
          className={`w-full text-sm rounded-md p-3 outline-none border-0 font-mono leading-relaxed ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`}
        />
        <div className="flex items-center justify-between gap-2 mt-3">
          <p className={`text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} data-testid="rs-bulk-import-count">
            {bulkUrlsList().length} valid URL{bulkUrlsList().length === 1 ? "" : "s"} detected
            {bulkUrlsList().length > 50 ? ` — first 50 will be processed` : ""}
          </p>
          <div className="flex items-center gap-2">
            {bulkUrls && !bulkImporting && (
              <button
                onClick={() => { setBulkUrls(""); setBulkResult(null); }}
                data-testid="rs-bulk-import-clear-btn"
                className={`px-3 py-2 rounded-md category-tag transition-colors ${dm ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]"}`}
              >
                Clear
              </button>
            )}
            <button
              onClick={bulkImport}
              disabled={!bulkUrlsList().length || bulkImporting}
              data-testid="rs-bulk-import-btn"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
            >
              {bulkImporting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ListPlus className="w-3.5 h-3.5" />}
              {bulkImporting ? "Importing…" : `Import ${Math.min(bulkUrlsList().length, 50) || ""}`}
            </button>
          </div>
        </div>

        {/* Result summary */}
        {bulkResult && (
          <div className={`mt-4 rounded-md p-3 ${dm ? "bg-white/5" : "bg-[#f3f4f5]"}`} data-testid="rs-bulk-import-result">
            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`category-tag px-2 py-1 rounded ${dm ? "bg-emerald-400/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
                {bulkResult.imported_count} imported
              </span>
              <span className={`category-tag px-2 py-1 rounded ${dm ? "bg-amber-400/10 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
                {bulkResult.duplicate_count} duplicate
              </span>
              {bulkResult.error_count > 0 && (
                <span className={`category-tag px-2 py-1 rounded ${dm ? "bg-rose-400/10 text-rose-300" : "bg-rose-50 text-rose-700"}`}>
                  {bulkResult.error_count} error{bulkResult.error_count === 1 ? "" : "s"}
                </span>
              )}
              {bulkResult.skipped_overflow_count > 0 && (
                <span className={`category-tag px-2 py-1 rounded ${dm ? "bg-slate-400/10 text-slate-300" : "bg-slate-100 text-slate-600"}`} data-testid="rs-bulk-overflow-badge">
                  {bulkResult.skipped_overflow_count} skipped (over 50-cap)
                </span>
              )}
            </div>
            {bulkResult.error_count > 0 && (
              <details className={`text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <summary className="cursor-pointer mb-1">Show errors</summary>
                <ul className="ml-4 list-disc space-y-1">
                  {(bulkResult.errors || []).slice(0, 10).map((e, i) => (
                    <li key={i} data-testid={`rs-bulk-error-${i}`}>
                      <span className="font-mono break-all">{e.url}</span> — {e.reason || "unknown"}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Source list card */}
      <div className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
        <div className={`px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap ${dm ? "border-white/5" : "border-black/4"}`}>
          <div className="flex items-center gap-2">
            <BookOpen className={`w-4 h-4 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} />
            <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Report &amp; Research Sources</p>
            <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{filtered.length} of {sources.length}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="rs-reload-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]"}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowAdd((v) => !v)}
              data-testid="rs-add-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md category-tag transition-colors ${
                showAdd
                  ? (dm ? "bg-cyan-400/20 text-cyan-300" : "bg-[#006874]/15 text-[#006874]")
                  : (dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]")
              }`}
            >
              {showAdd ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {showAdd ? "Close" : "Add Source"}
            </button>
          </div>
        </div>

        {/* Add form */}
        {showAdd && (
          <div className={`px-5 py-4 border-b ${dm ? "border-white/5 bg-white/3" : "border-black/4 bg-[#fafbfc]"}`}>
            <SourceForm
              dm={dm} form={addForm} setForm={setAddForm}
              onSave={createSource}
              onCancel={() => { setShowAdd(false); setAddForm(EMPTY_FORM); }}
              saving={saving} idPrefix="rs-add"
            />
          </div>
        )}

        {/* Search + priority filter */}
        <div className={`px-5 py-3 border-b flex items-center gap-2 flex-wrap ${dm ? "border-white/5" : "border-black/4"}`}>
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by organisation, category, focus…"
              data-testid="rs-search-input"
              className={`text-sm border-0 pl-9 ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`}
            />
          </div>
          <div className="flex gap-1">
            {["all", "Alta", "Media", "Bassa"].map((p) => (
              <button
                key={p}
                onClick={() => setPriorityFilter(p)}
                data-testid={`rs-filter-${p.toLowerCase()}`}
                className={`px-3 py-1.5 rounded-md category-tag transition-colors ${
                  priorityFilter === p
                    ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]")
                    : (dm ? "bg-white/3 text-slate-400 hover:bg-white/5" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]")
                }`}
              >
                {p === "all" ? "All" : p}
              </button>
            ))}
          </div>
        </div>

        {/* Rows */}
        {loading && sources.length === 0 ? (
          <div className={`p-6 text-center category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Loading sources…</div>
        ) : filtered.length === 0 ? (
          <div className={`p-6 text-center category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} data-testid="rs-empty-state">
            {sources.length === 0 ? "No report sources configured yet — add the first one above." : "No sources match your search."}
          </div>
        ) : filtered.map((s) => {
          const ps = PRIORITY_STYLES[s.priority] || PRIORITY_STYLES.Bassa;
          const isEditing = editingId === s.id;
          const isDisabled = s.enabled === false;
          return (
            <div key={s.id} data-testid={`rs-row-${s.id}`} className={`border-b last:border-b-0 ${dm ? "border-white/4" : "border-black/3"} ${isDisabled ? "opacity-60" : ""}`}>
              <div className="px-5 py-3 flex items-center gap-3">
                <Switch
                  checked={s.enabled !== false}
                  onCheckedChange={(v) => updateSource(s.id, { enabled: v })}
                  data-testid={`rs-toggle-${s.id}`}
                  className="data-[state=checked]:bg-[#006874]"
                />
                <span
                  className="text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded flex-shrink-0"
                  style={{ background: dm ? ps.bgD : ps.bgL, color: dm ? ps.fgD : ps.fgL }}
                  data-testid={`rs-priority-${s.id}`}
                >
                  {s.priority || "—"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                    <span className="truncate">{s.organisation}</span>
                    {s.origin === "radar" && (
                      <span
                        className={`text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-800"}`}
                        title={s.notes || "Auto-created by the weekly Report Radar"}
                        data-testid={`rs-radar-badge-${s.id}`}
                      >
                        Radar
                      </span>
                    )}
                    {s.rss_url && (
                      <span
                        className={`text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${
                          s.last_rss_error
                            ? (dm ? "bg-rose-400/15 text-rose-300" : "bg-rose-50 text-rose-700")
                            : (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-50 text-emerald-700")
                        }`}
                        title={s.last_rss_error ? `RSS feed error: ${s.last_rss_error}` : (s.last_rss_polled_at ? `RSS polled ${new Date(s.last_rss_polled_at).toLocaleDateString()} · ${s.last_rss_entries ?? 0} entries` : "RSS feed (polled daily)")}
                        data-testid={`rs-rss-badge-${s.id}`}
                      >
                        RSS{s.last_rss_error ? " !" : ""}
                      </span>
                    )}
                  </p>
                  <p className={`text-[11px] truncate ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    {s.category_macro || "—"}{s.area ? ` · ${s.area}` : ""}{s.focus ? ` · ${s.focus}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => scanSource(s)}
                    disabled={scanningId !== null}
                    data-testid={`rs-scan-${s.id}`}
                    title="Scan now — targeted Firecrawl discovery on this source"
                    aria-label="Scan now"
                    className={`flex items-center gap-1 category-tag px-2 py-1.5 rounded-md transition-colors disabled:opacity-40 ${
                      scanningId === s.id
                        ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]")
                        : (dm ? "text-slate-400 hover:text-cyan-300 hover:bg-white/5" : "text-[#6b7a7d] hover:text-[#006874] hover:bg-[#f3f4f5]")
                    }`}
                  >
                    {scanningId === s.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />}
                    Scan
                  </button>
                  {(s.report_hub_url || s.homepage_url) && (
                    <a
                      href={s.report_hub_url || s.homepage_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`rs-open-${s.id}`}
                      title={s.report_hub_url ? "Open report hub" : "Open homepage"}
                      className={`flex items-center gap-1 category-tag px-2 py-1.5 rounded-md transition-colors ${dm ? "text-slate-400 hover:text-cyan-300" : "text-[#6b7a7d] hover:text-[#006874]"}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <button
                    onClick={() => (isEditing ? setEditingId(null) : startEdit(s))}
                    data-testid={`rs-edit-${s.id}`}
                    aria-label="Edit source"
                    className={`p-1.5 rounded-md transition-colors ${
                      isEditing
                        ? (dm ? "bg-cyan-400/20 text-cyan-300" : "bg-[#006874]/15 text-[#006874]")
                        : (dm ? "text-slate-500 hover:text-cyan-300" : "text-[#6b7a7d] hover:text-[#006874]")
                    }`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => deleteSource(s)}
                    data-testid={`rs-delete-${s.id}`}
                    aria-label="Delete source"
                    className={`p-1.5 rounded-md transition-colors ${dm ? "text-slate-500 hover:text-red-400" : "text-[#6b7a7d] hover:text-red-600"}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {/* Inline edit panel */}
              {isEditing && (
                <div className={`px-5 pb-4 pt-2 border-t ${dm ? "border-white/5 bg-white/3" : "border-black/5 bg-[#fafbfc]"}`} data-testid={`rs-edit-panel-${s.id}`}>
                  <SourceForm
                    dm={dm} form={editForm} setForm={setEditForm}
                    onSave={saveEdit}
                    onCancel={() => setEditingId(null)}
                    saving={saving} idPrefix={`rs-edit-${s.id}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
